import { useCallback, useEffect, useRef, useState } from "react";

import type { HashResponse } from "../workers/hash.worker";

export function useHashWorker() {
  const workerRef = useRef<Worker | null>(null);
  const idRef = useRef(0);
  const [digestHex, setDigestHex] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/hash.worker.ts", import.meta.url),
      { type: "module" }
    );
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent<HashResponse>) => {
      const msg = event.data;
      if (msg.id !== idRef.current) return; // stale
      if (msg.type === "progress") {
        setProgress(msg.total > 0 ? msg.loaded / msg.total : null);
      } else if (msg.type === "done") {
        setDigestHex(msg.digestHex);
        setProgress(null);
      } else {
        setError(msg.message);
        setProgress(null);
      }
    };
    return () => worker.terminate();
  }, []);

  const hashFile = useCallback((file: File, algo: string) => {
    const id = ++idRef.current;
    setDigestHex(null);
    setError(null);
    setProgress(file.size > 50 * 1024 * 1024 ? 0 : null);
    workerRef.current?.postMessage({ id, algo, file });
  }, []);

  const resetHash = useCallback(() => {
    idRef.current++;
    setDigestHex(null);
    setProgress(null);
    setError(null);
  }, []);

  return { digestHex, progress, error, hashFile, resetHash };
}
